import { SupportedLocale } from '@philips-internal/oswald-ui-constants';
import { scopedErrorMessage } from '@philips-internal/oswald-ui-utils';
function logError(definition, field, ...message) {
    const className = definition?.name ?? definition?.constructor.name ?? '';
    const fieldName = field ?? '';
    const where = className && fieldName
        ? `${className}.${fieldName}`
        : className + fieldName;
    scopedErrorMessage('decorators', where, ...message);
}
/**
* Helper to generate a validator to check the decorators meta info.
* There are other util methods too.
*/
class MetaValidator {
    /**
    * Create a validator to check the decorators meta info.
    */
    static create() {
        const meta = {};
        const self = {
            bool: (name, mandatory, depends) => newProp(name, 'boolean', mandatory, depends),
            custom: (name, mandatory, fn) => newProp(name, 'custom', mandatory, fn),
            number: (name, mandatory, depends) => newProp(name, 'number', mandatory, depends),
            str: (name, mandatory, depends) => newProp(name, 'string', mandatory, depends),
            validator: () => {
                return createValidator(meta);
            },
        };
        function newProp(name, type, mandatory = false, depends = []) {
            meta[name] = { depends, mandatory, type };
            return self;
        }
        return self;
    }
    /**
    * Validate if the decorator structure is right.
    * @param {function} fn the decorator being applyed.
    * @param {*} paramsValidator function that validate params.
    * @param {*} definition tha class receiving the decorator.
    * @param {*} params the received params.
    */
    static validateDecorator(fn, paramsValidator, definition, params = {}) {
        const name = fn.name;
        MetaValidator.checkDef(definition, name);
        if (definition.constructor === Object) {
            return (...args) => fn(...args, definition);
        }
        paramsValidator(params, definition, name);
    }
    /**
    * Check if decorator is being invoked without params.
    *
    * @param {class} definition the receiving the decorator.
    * @param {*} name decorator name.
    */
    static checkDef(definition, name) {
        if (!definition) {
            logError(undefined, name, 'If there are no params, remove the ().');
        }
    }
}
function createValidator(knewProps) {
    const props = Object.getOwnPropertyNames(knewProps);
    const mandatories = props.filter(p => knewProps[p].mandatory);
    return (foundParams, definition, field) => {
        validateParams(knewProps, mandatories, foundParams, definition, field);
    };
}
function validateParams(knewProps, mandatories, foundParams, definition, field = '') {
    if (field) {
        field = `[${field}]`;
    }
    const props = Object.getOwnPropertyNames(foundParams);
    props.forEach(prop => {
        const tp = knewProps[prop];
        if (!tp) {
            logError(definition, field, `unknown property '${prop}'`);
        }
        if (tp.type === 'custom') {
            const valid = tp.depends(foundParams[prop], prop);
            if (!valid) {
                logError(definition, field, 'invalid prop value.');
            }
            return;
        }
        const typeOfParam = typeof foundParams[prop];
        if (tp && typeOfParam !== tp.type) {
            logError(definition, field, `expected property '${prop}' as '${tp.type}', found '${typeOfParam}'`);
        }
        if (tp && tp.depends) {
            tp.depends.forEach((dep) => {
                if (!props.includes(dep)) {
                    logError(definition, field, `when has '${prop}', props '${tp.depends}' are mandatory`);
                }
            });
        }
    });
    mandatories.forEach((prop) => {
        if (foundParams[prop] === undefined) {
            logError(definition, field, `mandatory property '${prop}' not found`);
        }
    });
}
function validateLocales(value) {
    if (Array.isArray(value)) {
        const supportedLocales = Object.values(SupportedLocale);
        return value.every((v) => supportedLocales.includes(v));
    }
    return false;
}
export default MetaValidator;
export { validateLocales, };
//# sourceMappingURL=meta-validator.js.map